import React, { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';

import { SimpleImage } from '$partials/media';
import IconCircle from '$partials/icons/icon-circle';
import ModuleContainer from '$partials/modules/container.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').AccordionItem}
 */
const AccordionItem = ({
    children,
    heading,
}) => {
    const [collapsibleOpen, setCollapsibleOpen] = useState(false);

    return (
        <div>
            <Collapsible.Root
                onOpenChange={setCollapsibleOpen}
                open={collapsibleOpen}>
                <div className='expand-interaction flex w-full justify-between items-center'>
                    <h4 className='type-style-heading-2'>
                        {heading}
                    </h4>

                    <Collapsible.Trigger asChild>
                        <button
                            className='expand-interaction__action group/button'
                            type='button'>
                            <div className='group-hover/button:text-purple transition-colors mr-12'>
                                {!collapsibleOpen && '↓'}
                                {collapsibleOpen && '↑'}
                            </div>
                        </button>
                    </Collapsible.Trigger>
                </div>

                <Collapsible.Content>
                    {children}
                </Collapsible.Content>
            </Collapsible.Root>
        </div>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').AccordionBlock}
 */
const AccordionBlock = ({
    heading,
    items
}) => {
    return (
        <ModuleContainer heading={heading}>
            <ul className='mb-30'>
                {items.map((item, i) => {
                    return (
                        <li
                            className='border-t first:border-t-0 border-black py-18 first:pt-0'
                            key={i}>
                            <AccordionItem heading={item.heading}>
                                <div className='flex flex-col gap-12 pt-12'>
                                    {item.content.map((contentItem, i) => {
                                        return (
                                            <React.Fragment key={i}>
                                                {(contentItem.__typename === 'AccordionTextBlock') &&
                                                    <div
                                                        className='rich-text max-w-text-length mr-6'
                                                        dangerouslySetInnerHTML={{ __html: contentItem.text }} />
                                                }

                                                {(contentItem.__typename === 'AccordionMediaBlock') &&
                                                    <div className='relative overflow-hidden rounded max-w-text-length'>
                                                        {contentItem?.media?.image &&
                                                            <SimpleImage
                                                                {...contentItem?.media.image}
                                                                className='w-full h-auto' />
                                                        }
                                                        {contentItem?.media?.video &&
                                                            <video
                                                                autoPlay={true}
                                                                className='w-full h-auto pointer-events-none'
                                                                loop={true}
                                                                muted={true}
                                                                playsInline={true}
                                                                poster={contentItem?.media.video.poster?.url}
                                                                src={contentItem?.media.video.src} />
                                                        }
                                                    </div>
                                                }

                                                {(contentItem.__typename === 'AccordionTimelineBlock') &&
                                                    <ul>
                                                        {contentItem.items?.map((timelineItem, i) => {
                                                            return (
                                                                <li
                                                                    className='relative flex mr-6 group/timeline'
                                                                    key={i}>
                                                                    <div
                                                                        aria-hidden={true}
                                                                        className='group-last/timeline:hidden absolute top-[6px] bottom-[-6px] left-[9.5px] w-[1px] bg-black' />
                                                                    <div
                                                                        aria-hidden={true}
                                                                        className='flex-shrink-0 w-[30px]'>
                                                                        <IconCircle className='w-[20px] h-[20px] mt-6' />
                                                                    </div>
                                                                    <div className='mb-12 max-w-text-length'>
                                                                        <h5 className='type-style-heading-2'>{timelineItem.heading}</h5>
                                                                        <div dangerouslySetInnerHTML={{ __html: timelineItem.text }} />
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                }
                                            </React.Fragment>
                                        );
                                    })}

                                </div>
                            </AccordionItem>
                        </li>
                    );
                })}
            </ul>
        </ModuleContainer>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default AccordionBlock;
